import { useContext, useEffect, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { useRouteMatch } from 'react-router-dom'

import { OrganizationContext } from 'components'
import { useApplications } from 'hooks'
import { Application } from 'types/Application'
import { TranslateFunction } from 'types/common'
import { GlobalSearchBarDetails } from 'types/GlobalSearchBarDetails'
import { AppLinks } from 'utils'
import { SidebarLinks } from 'utils/AppLinks'
import { APPLICATION_JIVE_BILLING } from 'utils/constants'
import useSupportLinks from './useSupportLinks'

export type FrequentSearch = { [searchKey: string]: number }

const sortGlobalSearchDetailsAlphabetically = (keyword1: GlobalSearchBarDetails, keyword2: GlobalSearchBarDetails) =>
  keyword1.keyword.localeCompare(keyword2.keyword)

const extractGlobalKeywordsFromAppLinks = (
  links: SidebarLinks,
  supportLinks: GlobalSearchBarDetails[],
  url: string,
  applicationGlobalKeywords?: GlobalSearchBarDetails,
) => {
  const computedGlobalKeywordItems: GlobalSearchBarDetails[] = applicationGlobalKeywords
    ? [applicationGlobalKeywords]
    : []

  Object.values(links || {}).forEach((linkKey) => {
    linkKey.forEach((link) => {
      if (link?.to && link.globalSearchBarDetails && link.globalSearchBarDetails.length > 0) {
        computedGlobalKeywordItems.push(
          ...link.globalSearchBarDetails.map((detail) => ({
            ...detail,
            to: link.external ? detail.to : `${url}${detail.to}`,
            external: detail.external || link.external,
          })),
        )
      }
    })
  })

  return [
    ...computedGlobalKeywordItems.sort(sortGlobalSearchDetailsAlphabetically),
    ...supportLinks.sort(sortGlobalSearchDetailsAlphabetically),
  ]
}

const extractFrequentSearchItemsFromPresetSuggestions = (
  computedGlobalKeywordItems: GlobalSearchBarDetails[],
  itemsFromSearchHistory: GlobalSearchBarDetails[],
) => {
  const computedFrequentlyUsedSearches = [...itemsFromSearchHistory]

  if (Object.keys(computedFrequentlyUsedSearches).length <= 5) {
    const foundItems = computedGlobalKeywordItems
      .filter((item) => item.suggestedScore)
      .sort((keyword1, keyword2) => keyword1.suggestedScore! - keyword2.suggestedScore!)

    foundItems.forEach((item) => {
      if (!computedFrequentlyUsedSearches.find((computedItem) => computedItem?.keyword === item.keyword)) {
        computedFrequentlyUsedSearches.push(item)
      }
    })
  }
  return computedFrequentlyUsedSearches
}

const extractGlobalKeywordsFromApplication = (
  applicationList: Application[],
  t: TranslateFunction,
  pbxDomain?: string,
): GlobalSearchBarDetails | undefined => {
  const billingApplication = applicationList.find((app) => app.id === APPLICATION_JIVE_BILLING)

  if (billingApplication) {
    return {
      to: `${billingApplication.url.replace(/\/+$/, '')}/${pbxDomain || ''}`,
      keyword: t('Billing'),
      external: true,
      tags: [t('Billing'), t('Payment'), t('Invoice'), t('How to'), t('How do')],
    }
  }
}

const extractFrequentSearchItemsFromUserHistory = (
  computedGlobalKeywordItems: GlobalSearchBarDetails[],
  frequentSearches: FrequentSearch,
) => {
  const computedFrequentlyUsedSearches: GlobalSearchBarDetails[] = []

  Object.keys(frequentSearches).forEach((searchKey) => {
    const matchingItem = computedGlobalKeywordItems.find((item) => item.keyword === searchKey)
    if (matchingItem) {
      computedFrequentlyUsedSearches.push(matchingItem)
    }
  })
  return computedFrequentlyUsedSearches
}

interface ExtractSearchDetailsProps {
  frequentSearches?: FrequentSearch
  integrateSupportLinks?: boolean
}

const useExtractSearchDetails = ({
  frequentSearches = undefined,
  integrateSupportLinks = true,
}: ExtractSearchDetailsProps): {
  globalKeywords: GlobalSearchBarDetails[]
  topFiveFrequentlySearched: GlobalSearchBarDetails[]
} => {
  const [globalKeywords, setGlobalKeywords] = useState<GlobalSearchBarDetails[]>([])
  const [topFiveFrequentlySearched, setTopFiveFrequentlySearched] = useState<GlobalSearchBarDetails[]>([])

  const links = AppLinks()
  const applicationList = useApplications()?.items || []
  const pbx = useContext(OrganizationContext)
  const { url } = useRouteMatch()
  const t = useTranslate()
  const appLinkCount = Object.values(links || {}).flat().length

  const supportLinks = useSupportLinks()

  useEffect(() => {
    setGlobalKeywords(
      extractGlobalKeywordsFromAppLinks(
        links,
        integrateSupportLinks ? supportLinks : [],
        url,
        extractGlobalKeywordsFromApplication(applicationList, t, pbx?.domain),
      ),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pbx?.domain, appLinkCount, applicationList])

  useEffect(() => {
    const itemsFromSearchHistory = frequentSearches
      ? extractFrequentSearchItemsFromUserHistory(globalKeywords, frequentSearches)
      : []

    const computedFrequentlyUsedSearches = extractFrequentSearchItemsFromPresetSuggestions(
      globalKeywords,
      itemsFromSearchHistory,
    )

    setTopFiveFrequentlySearched(
      computedFrequentlyUsedSearches.map((item) => ({ ...item, group: t('Frequently used') })).slice(0, 5),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalKeywords, frequentSearches])

  return { globalKeywords, topFiveFrequentlySearched }
}

export default useExtractSearchDetails
